import React, { useState, useEffect } from "react";
import { subscribeToRAStaff, verifyRAPIN, createRAStaff } from "./firebase";

const FACILITIES = ["Dorcus 1 - 801", "Dorcus 2 - 24th St"];

export default function RAPinScreen({ facility, onRALogin, onSwitchAccount }) {
  const [raStaff, setRaStaff]         = useState([]);
  const [selectedRA, setSelectedRA]   = useState(null);
  const [pin, setPin]                 = useState("");
  const [error, setError]             = useState("");
  const [loading, setLoading]         = useState(false);

  // Create new PIN state
  const [showCreate, setShowCreate]   = useState(false);
  const [newName, setNewName]         = useState("");
  const [newFacility, setNewFacility] = useState(facility || FACILITIES[0]);
  const [newPin, setNewPin]           = useState("");
  const [confirmPin, setConfirmPin]   = useState("");
  const [createError, setCreateError] = useState("");
  const [creating, setCreating]       = useState(false);
  const [createSuccess, setCreateSuccess] = useState("");

  useEffect(() => {
    // Load ALL RA staff — PINs are valid at both houses
    const unsub = subscribeToRAStaff(null, setRaStaff);
    return unsub;
  }, []);

  // ── PIN entry ──────────────────────────────────────────
  const handlePinDigit = (digit) => {
    if (pin.length >= 4) return;
    const newPin = pin + digit;
    setPin(newPin);
    setError("");
    if (newPin.length === 4) verifyPin(newPin);
  };

  const verifyPin = async (enteredPin) => {
    if (!selectedRA) return;
    setLoading(true);
    try {
      const valid = await verifyRAPIN(selectedRA.id, enteredPin);
      if (valid) {
        // Use the house computer's facility as the session facility
        onRALogin({ ...selectedRA, facility: facility || selectedRA.facility });
      } else {
        setError("Incorrect PIN. Try again.");
        setPin("");
      }
    } catch (e) {
      setError("Something went wrong. Try again.");
      setPin("");
    }
    setLoading(false);
  };

  const handleClear = () => { setPin(""); setError(""); };
  const handleBack  = () => { setSelectedRA(null); setPin(""); setError(""); };

  // ── Create new PIN ─────────────────────────────────────
  const handleCreate = async () => {
    setCreateError("");
    if (!newName.trim())                                     return setCreateError("Name is required.");
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin))     return setCreateError("PIN must be exactly 4 digits.");
    if (newPin !== confirmPin)                               return setCreateError("PINs do not match.");
    setCreating(true);
    await createRAStaff({ name:newName.trim(), facility:newFacility, pin:newPin });
    setCreateSuccess(`PIN created for ${newName.trim()}. You can now sign in.`);
    setNewName(""); setNewPin(""); setConfirmPin("");
    setCreating(false);
    setTimeout(() => { setShowCreate(false); setCreateSuccess(""); }, 2000);
  };

  const houseColor = "#38bdf8";

  return (
    <div style={{ minHeight:"100vh", background:"#020817", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans','Segoe UI',sans-serif", padding:16 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet"/>
      <div style={{ width:"100%", maxWidth:440, background:"#0f172a", border:`1px solid ${houseColor}33`, borderRadius:20, boxShadow:"0 25px 60px rgba(0,0,0,0.6)", overflow:"hidden" }}>

        {/* Header */}
        <div style={{ padding:"24px 28px 20px", borderBottom:"1px solid #1e293b", textAlign:"center" }}>
          <div style={{ width:48, height:48, borderRadius:14, background:"#4f46e5", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, margin:"0 auto 12px" }}>🏠</div>
          <div style={{ fontSize:18, fontWeight:700, color:"white" }}>Dorcus Home</div>
          <div style={{ fontSize:12, color:houseColor, marginTop:4, fontWeight:600 }}>{facility || "House Computer"}</div>
        </div>

        <div style={{ padding:28 }}>

          {/* ── Create new PIN screen ── */}
          {showCreate ? (
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20 }}>
                <button onClick={() => { setShowCreate(false); setCreateError(""); }} style={{ background:"transparent", border:"none", color:"#64748b", cursor:"pointer", fontSize:12 }}>← Back</button>
                <div style={{ fontSize:15, fontWeight:600, color:"white" }}>Create New RA PIN</div>
              </div>

              {createSuccess ? (
                <div style={{ textAlign:"center", padding:"24px 0" }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>✅</div>
                  <p style={{ color:"#4ade80", fontSize:14, fontWeight:600 }}>{createSuccess}</p>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  <div>
                    <div style={{ fontSize:10, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>Full Name *</div>
                    <input
                      style={{ width:"100%", background:"#1e293b", border:"1px solid #334155", borderRadius:10, padding:"10px 14px", fontSize:14, color:"white", outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
                      placeholder="Your name..." value={newName} onChange={e => setNewName(e.target.value)}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>Primary House *</div>
                    <select
                      style={{ width:"100%", background:"#1e293b", border:"1px solid #334155", borderRadius:10, padding:"10px 14px", fontSize:14, color:"white", outline:"none", boxSizing:"border-box", fontFamily:"inherit", cursor:"pointer" }}
                      value={newFacility} onChange={e => setNewFacility(e.target.value)}>
                      {FACILITIES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <div style={{ fontSize:10, color:"#475569", marginTop:4 }}>Your PIN will be valid at both houses.</div>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>4-Digit PIN *</div>
                    <input
                      type="password" inputMode="numeric" maxLength={4}
                      style={{ width:"100%", background:"#1e293b", border:"1px solid #334155", borderRadius:10, padding:"10px 14px", fontSize:18, color:"white", outline:"none", boxSizing:"border-box", fontFamily:"inherit", letterSpacing:"0.3em", textAlign:"center" }}
                      placeholder="····" value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g,"").slice(0,4))}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>Confirm PIN *</div>
                    <input
                      type="password" inputMode="numeric" maxLength={4}
                      style={{ width:"100%", background:"#1e293b", border:"1px solid #334155", borderRadius:10, padding:"10px 14px", fontSize:18, color:"white", outline:"none", boxSizing:"border-box", fontFamily:"inherit", letterSpacing:"0.3em", textAlign:"center" }}
                      placeholder="····" value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g,"").slice(0,4))}
                    />
                  </div>
                  {createError && <p style={{ fontSize:12, color:"#f87171", margin:0 }}>{createError}</p>}
                  <button
                    onClick={handleCreate} disabled={creating}
                    style={{ background:"#4f46e5", color:"white", border:"none", borderRadius:10, padding:"12px", fontSize:14, fontWeight:600, cursor:"pointer", opacity:creating?0.6:1, fontFamily:"inherit" }}>
                    {creating ? "Creating..." : "Create PIN"}
                  </button>
                </div>
              )}
            </div>

          ) : !selectedRA ? (
            /* ── Step 1: Select RA or create ── */
            <div>
              <p style={{ fontSize:13, color:"#64748b", margin:"0 0 16px", textAlign:"center" }}>Who is on duty?</p>

              {raStaff.length === 0 ? (
                <div style={{ textAlign:"center", padding:"24px 0" }}>
                  <div style={{ fontSize:32, marginBottom:10 }}>👤</div>
                  <p style={{ fontSize:13, color:"#475569" }}>No RAs have been set up yet.</p>
                  <p style={{ fontSize:11, color:"#334155", marginTop:4, marginBottom:20 }}>Create the first RA PIN below.</p>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
                  {raStaff.map(ra => (
                    <button key={ra.id} onClick={() => setSelectedRA(ra)} style={{
                      padding:"14px 18px", borderRadius:12, border:`1px solid ${houseColor}33`,
                      background:"#1e293b", cursor:"pointer", textAlign:"left",
                      display:"flex", alignItems:"center", gap:12,
                    }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = houseColor}
                      onMouseLeave={e => e.currentTarget.style.borderColor = `${houseColor}33`}
                    >
                      <div style={{ width:36, height:36, borderRadius:10, background:"#1e3a5f", border:"1px solid #1d4ed8", display:"flex", alignItems:"center", justifyContent:"center", color:"#93c5fd", fontWeight:700, fontSize:13, flexShrink:0 }}>
                        {ra.name.split(" ").map(n=>n[0]).slice(0,2).join("").toUpperCase()}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:600, color:"white" }}>{ra.name}</div>
                        <div style={{ fontSize:11, color:"#475569", marginTop:2 }}>{ra.facility}</div>
                      </div>
                      <span style={{ fontSize:11, color:"#475569" }}>Enter PIN →</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Create new PIN button */}
              <button onClick={() => setShowCreate(true)} style={{
                width:"100%", padding:"12px", borderRadius:12, border:"1px dashed #334155",
                background:"transparent", color:"#64748b", cursor:"pointer", fontSize:13,
                fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = houseColor; e.currentTarget.style.color = houseColor; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#334155"; e.currentTarget.style.color = "#64748b"; }}
              >
                + Create New RA PIN
              </button>
            </div>

          ) : (
            /* ── Step 2: Enter PIN ── */
            <div style={{ textAlign:"center" }}>
              <button onClick={handleBack} style={{ background:"transparent", border:"none", color:"#64748b", cursor:"pointer", fontSize:12, marginBottom:16, display:"flex", alignItems:"center", gap:4 }}>
                ← Back
              </button>
              <div style={{ width:52, height:52, borderRadius:14, background:"#1e3a5f", border:"1px solid #1d4ed8", display:"flex", alignItems:"center", justifyContent:"center", color:"#93c5fd", fontWeight:700, fontSize:18, margin:"0 auto 10px" }}>
                {selectedRA.name.split(" ").map(n=>n[0]).slice(0,2).join("").toUpperCase()}
              </div>
              <p style={{ fontSize:15, fontWeight:600, color:"white", margin:"0 0 4px" }}>{selectedRA.name}</p>
              <p style={{ fontSize:12, color:"#64748b", margin:"0 0 24px" }}>Enter your 4-digit PIN</p>

              {/* PIN dots */}
              <div style={{ display:"flex", justifyContent:"center", gap:12, marginBottom:24 }}>
                {[0,1,2,3].map(i => (
                  <div key={i} style={{
                    width:16, height:16, borderRadius:"50%",
                    background: i < pin.length ? houseColor : "#334155",
                    border:`2px solid ${i < pin.length ? houseColor : "#475569"}`,
                    transition:"all 0.15s",
                  }}/>
                ))}
              </div>

              {error   && <p style={{ fontSize:12, color:"#f87171", margin:"0 0 16px" }}>{error}</p>}
              {loading && <p style={{ fontSize:12, color:"#64748b", margin:"0 0 16px" }}>Verifying...</p>}

              {/* Numpad */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, maxWidth:240, margin:"0 auto" }}>
                {[1,2,3,4,5,6,7,8,9].map(n => (
                  <button key={n} onClick={() => handlePinDigit(String(n))} disabled={loading} style={{
                    padding:"18px", fontSize:20, fontWeight:600, borderRadius:12,
                    background:"#1e293b", border:"1px solid #334155", color:"white", cursor:"pointer",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background="#2d3748"; e.currentTarget.style.borderColor=houseColor; }}
                    onMouseLeave={e => { e.currentTarget.style.background="#1e293b"; e.currentTarget.style.borderColor="#334155"; }}
                  >
                    {n}
                  </button>
                ))}
                <button onClick={handleClear} disabled={loading} style={{ padding:"18px", fontSize:13, fontWeight:600, borderRadius:12, background:"transparent", border:"1px solid #334155", color:"#64748b", cursor:"pointer" }}>
                  Clear
                </button>
                <button onClick={() => handlePinDigit("0")} disabled={loading} style={{ padding:"18px", fontSize:20, fontWeight:600, borderRadius:12, background:"#1e293b", border:"1px solid #334155", color:"white", cursor:"pointer" }}
                  onMouseEnter={e => { e.currentTarget.style.background="#2d3748"; e.currentTarget.style.borderColor=houseColor; }}
                  onMouseLeave={e => { e.currentTarget.style.background="#1e293b"; e.currentTarget.style.borderColor="#334155"; }}
                >
                  0
                </button>
                <button onClick={handleBack} disabled={loading} style={{ padding:"18px", fontSize:13, fontWeight:600, borderRadius:12, background:"transparent", border:"1px solid #334155", color:"#64748b", cursor:"pointer" }}>
                  ←
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"14px 28px", borderTop:"1px solid #1e293b", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:11, color:"#334155" }}>Signed in as house computer</span>
          <button onClick={onSwitchAccount} style={{ background:"transparent", border:"none", color:"#475569", fontSize:11, cursor:"pointer" }}>
            Switch account
          </button>
        </div>
      </div>
    </div>
  );
}
